import { Component } from 'react';
import { Field, reduxForm } from 'redux-form';
import { locationShape } from 'react-router';
import { replace, goBack } from 'react-router-redux';
import { connect } from 'react-redux';
import moment from 'moment';
import lodash from 'lodash';
import { convertFromRaw } from 'draft-js';
import { UserHasEmailRemindersFeatureAccess } from 'common/features';
import {
    addApplicationHeaderTitle
} from 'common/actions/applicationHeader';

import {
    loadSDFADocumentBinderUserData,
    reloadUsers,
    submitSdfa,
    clearSdfa
} from 'forms/actions/sdfa';

import {
    documentActionsSelector,
    usersListSelector,
    binderTitleSelector,
    documentNameSelector,
    internalUsersListSelector,
} from 'forms/selectors/sdfa';

import {
    renderInputField,
    renderSingleDatePicker,
    renderSelectField
} from 'common/components/Form/Form';

import UsersSelector from 'forms/components/Sdfa/UsersSelector/UsersSelector';
import RichTextEditor from 'forms/components/Sdfa/RichTextEditor';
import LoadingComponent from 'common/permissions/components/LoadingComponent';
import formStyles from 'styles/forms.scss';
import styles from './SdfaStart.scss';

const MAX_SHORT_DESCRIPTION_LENGTH = 155;
const MAX_TITLE_LENGTH = 140;
export const documentUtil = {
    getDocumentReferrer: () => lodash.get(document, 'referrer', null)
};

class sdfaForm extends Component {
    render() {
        const {
            submitting,
            invalid,
            handleSubmit,
            documentActions,
            internalUsers,
            users,
            reloadUsers,
            onSubmit,
            binderId,
        } = this.props;
        const DueDateReminderField = UserHasEmailRemindersFeatureAccess(DueDateReminderFormField);
        const OverdueReminderDaysField = UserHasEmailRemindersFeatureAccess(OverdueReminderDaysFormField);
        return (
            <div className={styles.formContainer} data-cy="sdfaForm">
                <form className={formStyles.form} onSubmit={handleSubmit(onSubmit)} data-cy="sdfaForm-submit">
                    <Field
                        name="title"
                        type="text"
                        label="Title"
                        required={true}
                        autoFocus={true}
                        component={renderInputField} />
                    <Field
                        label="Due Date"
                        name="dueDate"
                        required={true}
                        component={renderSingleDatePicker} />
                    <DueDateReminderField renderSingleDatePicker={renderSingleDatePicker} />
                    <OverdueReminderDaysField renderSingleDatePicker={renderSingleDatePicker} />
                    <Field
                        name="documentAction"
                        label="Requested Action"
                        required={true}
                        component={renderSelectField}
                        options={documentActions} />
                    <Field
                        name="description"
                        label="Description"
                        labelAdditional=" - Optional"
                        required={false}
                        component={RichTextEditor} />
                    <Field
                        name="users"
                        component={UsersSelector}
                        internalUsers={internalUsers}
                        users={users}
                        binderId={binderId}
                        required={true}
                        reloadUsers={reloadUsers} />
                    <div className={styles.sendButtonWrapper}>
                        <button type="submit" className={`${formStyles.button} ${styles.actionButton}`} disabled={invalid || submitting} data-cy="sdfaForm-btn">
                            {submitting ? 'Sending...' : 'Send Document'}
                        </button>
                    </div>
                </form>
            </div>
        );
    }
}

const DueDateReminderFormField = ({ renderSingleDatePicker }) => (
    <Field
        label="Due Date Reminder"
        name="reminderDate"
        required={true}
        component={renderSingleDatePicker} />
);

const OverdueReminderDaysFormField = ({ renderSingleDatePicker }) => (
    <Field
        label="Overdue Reminder Days"
        placeholder="The system will send an email every [Overdue Reminder] days after the due date"
        name="dueReminderFrequency"
        type="number"
        component={renderInputField} />
);

export function validate(values) {
    const errors = {};

    if (!values.title || values.title.trim().length === 0) {
        errors.title = '* Required';
    }
    else if (values.title.length > MAX_TITLE_LENGTH) {
        errors.title = `* The title may not exceed ${MAX_TITLE_LENGTH} characters`;
    }


    if (!values.documentAction) {
        errors.documentAction = '* Required';
    }

    if (!values.reminderDate) {
        errors.reminderDate = '* Required';
    }
    else {
        const reminderDate = moment(values.reminderDate);
        if (!reminderDate.isValid()) {
            errors.reminderDate = '* Invalid';
        }
    }

    if (!values.dueDate) {
        errors.dueDate = '* Required';
    }
    else {
        const dueDate = moment(values.dueDate);
        if (!dueDate.isValid()) {
            errors.dueDate = '* Invalid';
        }
    }

    const dueReminderFrequencyValue = lodash.toInteger(values.dueReminderFrequency);
    if (!values.dueReminderFrequency) {
        errors.dueReminderFrequency = '* Required';
    }
    else if (dueReminderFrequencyValue < 1 || dueReminderFrequencyValue > 14) {
        errors.dueReminderFrequency = '* Value must be between 1 and 14';
    }

    if (lodash.isNil(values.users) || lodash.isEmpty(values.users)) {
        errors.users = '* Required';
    }

    return errors;
}

const SDFA = reduxForm({
    form: 'sdfa',
    validate,
    initialValues: {
        dueReminderFrequency: 1
    }
})(sdfaForm);


const SdfaContext = (props) => (
    <div className={props.className}>
        {props.documentName && <h2>{props.documentName}</h2>}
        {props.binderTitle && <h3>from: {props.binderTitle}</h3>}
    </div>
);

export class SdfaContainer extends Component {
    componentWillMount() {
        const { documentId, goBack, addApplicationHeaderTitle } = this.props;

        addApplicationHeaderTitle('Send Document for Action');
        if (lodash.isNull(documentId)) {
            goBack();
        }
    }

    componentWillUnmount() {
        this.props.clearSdfa();
    }

    componentDidMount() {
        const { binderId, documentId } = this.props;
        this.props.loadSDFADocumentBinderUserData(binderId, documentId);
    }

    componentWillUpdate(nextProps) {
        const { shouldRedirect } = nextProps;
        const { replace, goBack, documentId, binderId } = this.props;

        if (shouldRedirect) {
            const referrer = documentUtil.getDocumentReferrer();

            if (!lodash.isEmpty(referrer)) {
                goBack();
            } else {
                if (binderId) {
                    replace(`/binder/${binderId}/files/${documentId}`);
                } else {
                    goBack();
                }
            }
        }
    }

    static getShortDescription(description) {
        const contentState = convertFromRaw(description);
        const contentBlocks = contentState.getBlockMap();
        let shortDescription = '';
        const blockSeparator = ' ';
        for (let contentBlock of contentBlocks.values()) {
            const blockText = contentBlock.getText();
            if (blockText.trim().length === 0) {
                continue;
            }
            const blockLength = blockText.length;
            if (shortDescription.length + blockLength + blockSeparator.length > MAX_SHORT_DESCRIPTION_LENGTH) {
                shortDescription += blockText;
                shortDescription = shortDescription.substr(0, MAX_SHORT_DESCRIPTION_LENGTH);
                break;
            }
            shortDescription += blockText + blockSeparator;
        }
        return lodash.trimEnd(shortDescription, blockSeparator);
    }

    onSubmit({
        description,
        documentAction,
        dueDate,
        reminderDate,
        title,
        dueReminderFrequency = 7,
        users = []
    }) {
        const { documentId, binderId, studyId, submit } = this.props;

        if (!dueReminderFrequency) {
            dueReminderFrequency = 1;
        }
        else {
            dueReminderFrequency = lodash.toInteger(dueReminderFrequency);
        }

        const verifiedReminderDate = reminderDate ? reminderDate : dueDate;

        const shortDescription = SdfaContainer.getShortDescription(description);

        const sdfaRequest = {
            actionName: documentAction,
            recipients: users.map(({ sub }) => sub),
            richTextDescription: JSON.stringify(description),
            documentId,
            binderId,
            studyId,
            title,
            dueDate,
            reminderDate: verifiedReminderDate,
            shortDescription,
            dueReminderFrequency,
        };

        return submit(sdfaRequest);
    }

    render() {
        const { reloadUsers, users, documentActions, binderId, documentName, binderTitle, internalUsers } = this.props;
        if (!users || !documentActions || !internalUsers) {
            return <LoadingComponent />;
        }

        return (
            <div>
                <SdfaContext
                    binderTitle={binderTitle}
                    documentName={documentName}
                    className={styles.sdfaContext} />
                <SDFA
                    onSubmit={this.onSubmit.bind(this)}
                    reloadUsers={reloadUsers.bind(this)}
                    users={users}
                    internalUsers={internalUsers}
                    binderId={binderId}
                    documentActions={documentActions} />
            </div>
        );
    }
}

SdfaContainer.propTypes = {
    location: locationShape.isRequired
};

export default connect(
    (state, props) => ({
        binderId: lodash.get(props, 'location.hash', '').substring(1),
        users: usersListSelector(state),
        internalUsers: internalUsersListSelector(state),
        documentActions: documentActionsSelector(state),
        studyId: lodash.get(props, 'location.query.studyId', null),
        documentId: lodash.get(props, 'location.query.docid', null),
        documentName: documentNameSelector(state),
        binderTitle: binderTitleSelector(state),
        shouldRedirect: Boolean(lodash.get(state, 'sdfa.id', false))
    }), {
        loadSDFADocumentBinderUserData,
        reloadUsers,
        submit: submitSdfa,
        clearSdfa,
        addApplicationHeaderTitle,
        replace,
        goBack
    }
)(SdfaContainer);
